import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { NotificationService } from '../../services/notification.service';
import { AppointmentStatus, AppointmentStatusLabels } from '../../models/models';

@Component({
  selector: 'app-agenda',
  templateUrl: './agenda.component.html',
  styleUrls: ['./agenda.component.scss']
})
export class AgendaComponent implements OnInit, OnDestroy {

  professionalId = 0;
  currentDate  = new Date();
  selectedDate = new Date();

  appointments: any[] = [];
  patients:     any[] = [];
  availableSlots: any[] = [];
  workingHours:   any[] = [];

  // Seleção de slot no modal de novo agendamento
  modalDate    = '';
  modalSlots:  any[] = [];
  selectedSlot: any = null;

  viewMode: 'month' | 'week' | 'day' = 'month';
  loading = false;

  // Modal novo agendamento
  showNewAppointmentModal = false;
  saving = false;
  newAppointment: any = {};
  modalDateDisplay = ''; // Data no formato dd/MM/yyyy para exibição

  // Datepicker
  showDatePicker = false;
  pickerCurrentDate = new Date();
  pickerDays: Date[] = [];
  pickerTop = '0px';
  pickerLeft = '0px';

  @ViewChild('dateInput', { read: ElementRef }) dateInput?: ElementRef;

  // Modal detalhes
  selectedAppointment: any = null;
  showDetailsModal = false;
  savingStatus = false;

  // Status — tipado como Record<number, string> para aceitar indexação com 'any'
  statusLabels: Record<number, string> = AppointmentStatusLabels;
  appointmentStatus = AppointmentStatus;

  weekDays    = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  calendarDays: Date[] = [];

  private subs = new Subscription();

  constructor(
    private api: ApiService,
    private profState: ProfessionalStateService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.generateCalendar();

    this.subs.add(
      this.profState.selected$.subscribe(p => {
        if (p) {
          this.professionalId = p.id;
          this.loadPatients();
          this.loadAppointments();
          this.loadWorkingHours();
        }
      })
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  // ── Pacientes para o selectbox ──────────────────────────────────
  loadPatients(): void {
    this.api.getPatients(this.professionalId, { page: 1, pageSize: 500, isActive: true }).subscribe({
      next:  r  => this.patients = r.items,
      error: () => {}
    });
  }

  // ── Agendamentos do mês ─────────────────────────────────────────
  loadAppointments(): void {
    if (!this.professionalId) return;
    this.loading = true;
    const start = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
    const end   = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0, 23, 59, 59, 999);

    this.api.getAppointments(this.professionalId, {
      startDate: start.toISOString(),
      endDate:   end.toISOString(),
      pageSize:  500
    }).subscribe({
      next:  r  => { this.appointments = r.items; this.loading = false; },
      error: () => { this.loading = false; }
    });
  }

  // ── Calendário ─────────────────────────────────────────────────
  generateCalendar(): void {
    this.calendarDays = [];
    const year  = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay  = new Date(year, month + 1, 0);

    for (let i = firstDay.getDay() - 1; i >= 0; i--) {
      this.calendarDays.push(new Date(year, month, -i));
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      this.calendarDays.push(new Date(year, month, d));
    }
    const remaining = 42 - this.calendarDays.length;
    for (let d = 1; d <= remaining; d++) {
      this.calendarDays.push(new Date(year, month + 1, d));
    }
  }

  previousMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.generateCalendar();
    this.loadAppointments();
  }

  nextMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.generateCalendar();
    this.loadAppointments();
  }

  // ── Horários de atendimento ─────────────────────────────────────
  loadWorkingHours(): void {
    if (!this.professionalId) return;
    this.api.getWorkingHours(this.professionalId).subscribe({
      next:  wh => this.workingHours = wh,
      error: ()  => this.workingHours = []
    });
  }

  // ── Geração de slots disponíveis ───────────────────────────────
  // Suporta múltiplos blocos por dia (manhã + tarde) e intervalMinutes
  private computeSlotsForDate(date: Date): any[] {
    const dow       = date.getDay();
    const dayBlocks = this.workingHours.filter(h => h.dayOfWeek === dow && h.isActive);
    if (dayBlocks.length === 0) return [];

    const dayAppts = this.getAppointmentsForDate(date);
    const slots: any[] = [];

    for (const wh of dayBlocks) {
      const [sh, sm] = (wh.startTime as string).split(':').map(Number);
      const [eh, em] = (wh.endTime   as string).split(':').map(Number);
      const endMs    = new Date(date).setHours(eh, em, 0, 0);
      const step     = (wh.slotDurationMinutes + (wh.intervalMinutes || 0)) * 60000;

      const cur = new Date(date);
      cur.setHours(sh, sm, 0, 0);

      while (cur.getTime() < endMs) {
        const slotStart = new Date(cur);
        const slotEnd   = new Date(cur.getTime() + wh.slotDurationMinutes * 60000);
        if (slotEnd.getTime() > endMs) break;

        const booked = dayAppts.some(a => {
          // 4=Finalizado 5=Cancelado 6=NãoCompareceu — liberam o horário
          if ([4, 5, 6].includes(a.status)) return false;
          const aStart = new Date(a.startTime).getTime();
          const aEnd   = new Date(a.endTime).getTime();
          return slotStart.getTime() < aEnd && slotEnd.getTime() > aStart;
        });

        if (!booked) {
          // Usa horário local (sem conversão UTC) para evitar deslocamento de fuso
          slots.push({ startTime: this.localISO(slotStart), endTime: this.localISO(slotEnd) });
        }
        cur.setTime(cur.getTime() + step);
      }
    }

    slots.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    return slots;
  }

  // Monta string ISO sem converter para UTC — preserva horário local
  private localISO(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}` +
           `T${p(d.getHours())}:${p(d.getMinutes())}:00`;
  }

  computeAvailableSlots(date: Date): void {
    this.availableSlots = this.computeSlotsForDate(date);
  }

  // ── Slots no modal ──────────────────────────────────────────────
  loadModalSlots(): void {
    if (!this.modalDate) { this.modalSlots = []; return; }
    const date = new Date(this.modalDate + 'T00:00:00');
    this.modalSlots = this.computeSlotsForDate(date);
  }

  onModalDateChange(): void {
    this.selectedSlot = null;
    this.newAppointment.startTime = '';
    this.newAppointment.endTime   = '';
    this.loadModalSlots();
  }

  // ── Datepicker ──────────────────────────────────────────────────
  toggleDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;

    if (this.showDatePicker) {
      // Se já tem data selecionada, usa ela, senão usa data atual
      if (this.modalDate) {
        const parts = this.modalDate.split('-');
        this.pickerCurrentDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        this.pickerCurrentDate = new Date();
      }
      this.generatePickerCalendar();

      // Calcular posição do datepicker
      setTimeout(() => {
        if (this.dateInput) {
          const rect = this.dateInput.nativeElement.getBoundingClientRect();
          this.pickerTop = `${rect.bottom + 8}px`;
          this.pickerLeft = `${rect.left}px`;
        }
      }, 0);
    }
  }

  generatePickerCalendar(): void {
    this.pickerDays = [];
    const year = this.pickerCurrentDate.getFullYear();
    const month = this.pickerCurrentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Preenche dias do mês anterior
    for (let i = firstDay.getDay(); i > 0; i--) {
      this.pickerDays.push(new Date(year, month, 1 - i));
    }

    // Dias do mês atual
    for (let d = 1; d <= lastDay.getDate(); d++) {
      this.pickerDays.push(new Date(year, month, d));
    }

    // Dias do próximo mês para completar a grade
    const remaining = 42 - this.pickerDays.length;
    for (let d = 1; d <= remaining; d++) {
      this.pickerDays.push(new Date(year, month + 1, d));
    }
  }

  pickerPreviousMonth(): void {
    this.pickerCurrentDate = new Date(this.pickerCurrentDate.getFullYear(), this.pickerCurrentDate.getMonth() - 1, 1);
    this.generatePickerCalendar();
  }

  pickerNextMonth(): void {
    this.pickerCurrentDate = new Date(this.pickerCurrentDate.getFullYear(), this.pickerCurrentDate.getMonth() + 1, 1);
    this.generatePickerCalendar();
  }

  pickerSelectDate(date: Date): void {
    const pad = (n: number) => String(n).padStart(2, '0');

    // Formato yyyy-MM-dd (ISO)
    this.modalDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

    // Formato dd/MM/yyyy (brasileiro)
    this.modalDateDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

    this.showDatePicker = false;
    this.onModalDateChange();
  }

  pickerSelectToday(): void {
    this.pickerSelectDate(new Date());
  }

  pickerIsCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.pickerCurrentDate.getMonth();
  }

  pickerIsToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  pickerIsSelected(date: Date): boolean {
    if (!this.modalDate) return false;
    const parts = this.modalDate.split('-');
    const selectedDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  }

  getPickerMonthYear(): string {
    return this.pickerCurrentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  // Conversão de data dd/MM/yyyy → yyyy-MM-dd (mantido para digitação manual)
  onDateInput(event: any): void {
    let value = event.target.value.replace(/\D/g, ''); // Remove não-dígitos

    // Aplica máscara dd/MM/yyyy
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    this.modalDateDisplay = value;

    // Converte para formato yyyy-MM-dd quando completo
    if (value.length === 10) {
      const parts = value.split('/');
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      // Validação básica
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900) {
        const pad = (n: number) => String(n).padStart(2, '0');
        this.modalDate = `${year}-${pad(month)}-${pad(day)}`;
        this.onModalDateChange();
      } else {
        this.modalDate = '';
        this.modalSlots = [];
      }
    } else {
      this.modalDate = '';
      this.modalSlots = [];
    }
  }

  selectSlot(slot: any): void {
    this.selectedSlot = slot;
    this.newAppointment.startTime = slot.startTime;
    this.newAppointment.endTime   = slot.endTime;
  }

  selectDate(date: Date): void {
    this.selectedDate = new Date(date);
    this.viewMode = 'day';
    this.computeAvailableSlots(date);
  }

  isCurrentMonth(date: Date): boolean { return date.getMonth() === this.currentDate.getMonth(); }
  isToday(date: Date): boolean {
    const t = new Date();
    return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
  }
  isSelected(date: Date): boolean {
    return date.getDate() === this.selectedDate.getDate() &&
           date.getMonth() === this.selectedDate.getMonth() &&
           date.getFullYear() === this.selectedDate.getFullYear();
  }

  getAppointmentsForDate(date: Date): any[] {
    return this.appointments.filter(a => {
      const d = new Date(a.startTime);
      return d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
    });
  }

  getAppointmentsForSelectedDate(): any[] { return this.getAppointmentsForDate(this.selectedDate); }

  // ── Modal novo agendamento ──────────────────────────────────────
  openNewAppointmentModal(slot?: any): void {
    const baseDate = slot ? new Date(slot.startTime) : new Date(this.selectedDate);
    const pad      = (n: number) => String(n).padStart(2, '0');
    this.modalDate = `${baseDate.getFullYear()}-${pad(baseDate.getMonth()+1)}-${pad(baseDate.getDate())}`;

    // Converte para formato brasileiro dd/MM/yyyy
    this.modalDateDisplay = `${pad(baseDate.getDate())}/${pad(baseDate.getMonth()+1)}/${baseDate.getFullYear()}`;

    this.newAppointment = { patientId: 0, serviceType: '', notes: '', syncWithGoogleCalendar: false,
                            startTime: '', endTime: '' };
    this.loadModalSlots();

    if (slot) {
      this.selectedSlot = this.modalSlots.find(s => s.startTime === slot.startTime) || slot;
      this.newAppointment.startTime = this.selectedSlot.startTime;
      this.newAppointment.endTime   = this.selectedSlot.endTime;
    } else {
      this.selectedSlot = null;
    }

    this.showNewAppointmentModal = true;
  }

  closeNewAppointmentModal(): void {
    this.showNewAppointmentModal = false;
    this.selectedSlot = null;
    this.modalSlots   = [];
  }

  createAppointment(): void {
    if (!this.newAppointment.patientId || this.newAppointment.patientId == 0) {
      this.notification.warning('Paciente Obrigatório', 'Por favor, selecione um paciente.');
      return;
    }
    if (!this.selectedSlot) {
      this.notification.warning('Horário Obrigatório', 'Por favor, selecione um horário disponível.');
      return;
    }
    this.saving = true;
    this.api.createAppointment(this.professionalId, this.newAppointment).subscribe({
      next: () => {
        this.saving = false;
        this.closeNewAppointmentModal();
        this.loadAppointments();
        this.notification.success('Agendamento Criado', 'O agendamento foi criado com sucesso!');
      },
      error: () => {
        this.saving = false;
        this.notification.error('Erro ao Criar', 'Não foi possível criar o agendamento.');
      }
    });
  }

  // ── Modal detalhes ──────────────────────────────────────────────
  viewAppointmentDetails(appointment: any): void {
    this.selectedAppointment = appointment;
    this.showDetailsModal    = true;
  }

  closeDetailsModal(): void { this.showDetailsModal = false; this.selectedAppointment = null; }

  finishAppointment(): void {
    if (!this.selectedAppointment) return;
    this.notification.confirm(
      'Finalizar Agendamento',
      'Deseja finalizar este agendamento? Um registro de contas a receber será gerado automaticamente.',
      () => {
        this.savingStatus = true;
        const updated = { ...this.selectedAppointment, status: AppointmentStatus.Completed };
        this.api.updateAppointment(this.professionalId, this.selectedAppointment.id, updated).subscribe({
          next: () => {
            const apptDate = new Date(this.selectedAppointment.startTime);
            const pad = (n: number) => String(n).padStart(2, '0');
            const isoDate = `${apptDate.getFullYear()}-${pad(apptDate.getMonth() + 1)}-${pad(apptDate.getDate())}`;
            const ar = {
              patientId: this.selectedAppointment.patientId,
              description: 'Consulta' + (this.selectedAppointment.serviceType ? ' - ' + this.selectedAppointment.serviceType : ''),
              amount: 0,
              dueDate: isoDate,
              competenceDate: isoDate,
              status: 'Pendente'
            };
            this.api.createAccountReceivable(this.professionalId, ar).subscribe({
              next: () => {
                this.savingStatus = false;
                this.loadAppointments();
                this.closeDetailsModal();
                this.notification.success('Agendamento Finalizado', 'Agendamento finalizado e contas a receber gerado com sucesso.');
              },
              error: () => {
                this.savingStatus = false;
                this.loadAppointments();
                this.closeDetailsModal();
                this.notification.warning('Atenção', 'Agendamento finalizado, mas houve um erro ao gerar o contas a receber.');
              }
            });
          },
          error: () => {
            this.savingStatus = false;
            this.notification.error('Erro ao Finalizar', 'Não foi possível finalizar o agendamento.');
          }
        });
      }
    );
  }

  cancelAppointment(): void {
    if (!this.selectedAppointment) return;
    this.notification.confirm(
      'Cancelar Agendamento',
      'Deseja realmente cancelar este agendamento?',
      () => {
        this.savingStatus = true;
        const updated = { ...this.selectedAppointment, status: AppointmentStatus.Cancelled };
        this.api.updateAppointment(this.professionalId, this.selectedAppointment.id, updated).subscribe({
          next: () => {
            this.savingStatus = false;
            this.loadAppointments();
            this.closeDetailsModal();
            this.notification.success('Agendamento Cancelado', 'O agendamento foi cancelado com sucesso.');
          },
          error: () => {
            this.savingStatus = false;
            this.notification.error('Erro ao Cancelar', 'Não foi possível cancelar o agendamento.');
          }
        });
      },
      undefined,
      'warning'
    );
  }

  // ── Permissão de gerenciamento de status ────────────────────────
  get canManageStatus(): boolean {
    return this.profState.selected?.isOwner === true;
  }

  // ── Helpers ─────────────────────────────────────────────────────
  formatTime(date: Date | string): string {
    return new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('pt-BR');
  }

  getMonthYear(): string {
    return this.currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  getStatusClass(status: AppointmentStatus): string {
    const m: any = {
      [AppointmentStatus.Confirmed]: 'badge bg-success',
      [AppointmentStatus.Scheduled]: 'badge bg-warning text-dark',
      [AppointmentStatus.Completed]: 'badge bg-info',
      [AppointmentStatus.Cancelled]: 'badge bg-danger'
    };
    return m[status] || 'badge bg-secondary';
  }

}
